/**
 * MyYardageChartCard — shareable / downloadable "Bag IQ yardage chart".
 *
 * Renders a branded card containing the user's personal club yardages so they
 * can take it to the driving range, share to a mate, or pin to a coach.
 *
 *   - Data sources merged with the same precedence as ClubSuggestionChip:
 *       1. p50 from real dispersion (≥3 shots) — preferred
 *       2. Manual entry from the ClubBag page
 *       3. Hidden when neither is present (so we don't lie about distances)
 *   - Renders via html-to-image → blob, then either navigator.share (mobile)
 *     or download fallback. Same pattern as ShareRoundCard.
 *   - Hidden render — the visual card lives in an off-screen container that
 *     html-to-image snapshots at 2× pixel ratio for crisp output.
 */
import { useRef, useState, forwardRef } from "react";
import * as htmlToImage from "html-to-image";
import { Share, DownloadSimple, Sparkle, Golf } from "@phosphor-icons/react";
import { CLUB_CATALOG } from "../lib/clubs";

export default function MyYardageChartCard({ dispersion, clubYardages, userName }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  // Build the merged list once. Each row needs: label, primary yardage, source
  // and (when real data) a confidence range. We keep the CLUB_CATALOG ordering
  // (driver → putter) so the printed chart reads like a real bag list.
  const dispMap = {};
  (dispersion || []).forEach((d) => { dispMap[d.club_key] = d; });

  const rows = CLUB_CATALOG.map((c) => {
    const disp = dispMap[c.key];
    if (disp && disp.count >= 3) {
      return {
        key: c.key,
        label: c.label,
        yards: Math.round(disp.p50),
        range: `${Math.round(disp.p25)}–${Math.round(disp.p75)}`,
        count: disp.count,
        source: "personal",
      };
    }
    const manual = clubYardages?.[c.key];
    if (manual != null) {
      return {
        key: c.key,
        label: c.label,
        yards: Number(manual),
        range: null,
        count: 0,
        source: "manual",
      };
    }
    return null;
  }).filter(Boolean);

  if (rows.length === 0) return null;

  const buildBlob = async () => {
    if (!cardRef.current) return null;
    return await htmlToImage.toBlob(cardRef.current, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: "#143D2F",
    });
  };

  const share = async () => {
    setBusy(true);
    setToast("");
    try {
      const blob = await buildBlob();
      if (!blob) throw new Error("Could not render card");
      const filename = "fairway-bag-iq-yardages.png";
      const file = new File([blob], filename, { type: "image/png" });
      const text = `My Fairway Bag IQ yardages — built from my real shots 🏌️\nhttps://fairway-golf.org`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Fairway yardages", text });
        setToast("Shared!");
      } else {
        // Fallback to download.
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        setToast("Saved to downloads");
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.warn("MyYardageChartCard: share failed", err);
        setToast("Could not share — try again");
      }
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    setToast("");
    try {
      const blob = await buildBlob();
      if (!blob) throw new Error("Could not render card");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "fairway-bag-iq-yardages.png";
      a.click();
      URL.revokeObjectURL(url);
      setToast("Saved to downloads");
    } catch (err) {
      console.warn("MyYardageChartCard: download failed", err);
      setToast("Could not save — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-5" data-testid="my-yardages-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-bold text-lg text-[var(--primary)]">
          My yardages
        </h3>
        <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
          Range-ready chart
        </span>
      </div>
      <p className="text-xs text-[var(--text-secondary)] mb-3 leading-snug">
        A shareable summary of your personal club distances — perfect to take to the range, send to a mate, or pin in a coach chat.
      </p>

      {/* Preview tile + actions (the off-screen "real" card is rendered below) */}
      <div className="surface-card-flat p-4">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-3" data-testid="my-yardages-preview-grid">
          {rows.map((r) => (
            <div key={r.key} className="flex items-baseline justify-between text-sm">
              <span className="font-bold text-[var(--primary)]">{r.label}</span>
              <span className="font-mono-num text-[var(--text)]">
                {r.yards}
                <span className="text-[10px] opacity-60 ml-0.5">yds</span>
                {r.source === "personal" && (
                  <span className="text-[9px] text-[var(--primary)] opacity-70 ml-1">★</span>
                )}
              </span>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-[var(--text-secondary)] mb-3 leading-snug">
          <span className="text-[var(--primary)]">★</span> = learned from your real shots ·
          rest are manually-entered bag values.
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={share}
            disabled={busy}
            data-testid="my-yardages-share"
            className="btn-primary flex-1"
          >
            <Share size={16} weight="fill" /> {busy ? "Working…" : "Share with mates"}
          </button>
          <button
            type="button"
            onClick={download}
            disabled={busy}
            data-testid="my-yardages-download"
            className="btn-secondary"
            title="Save as image"
            aria-label="Download yardage chart"
          >
            <DownloadSimple size={16} weight="bold" />
          </button>
        </div>
        {toast && (
          <p className="text-xs text-[var(--text-secondary)] text-center mt-2" data-testid="my-yardages-toast">
            {toast}
          </p>
        )}
      </div>

      {/* Hidden "real" card that gets rasterised. Off-screen but visible to
          html-to-image (display:none would skip layout). */}
      <div
        style={{
          position: "fixed",
          left: -10000,
          top: 0,
          width: 720,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      >
        <PrintableCard
          ref={cardRef}
          rows={rows}
          userName={userName}
        />
      </div>
    </div>
  );
}

/**
 * The actual social-card layout that gets rasterised. Inline styles only —
 * Tailwind classes don't always reach html-to-image at render time because
 * the JIT may have purged them.
 */
const PrintableCard = forwardRef(function PrintableCard({ rows, userName }, ref) {
  const half = Math.ceil(rows.length / 2);
  const colA = rows.slice(0, half);
  const colB = rows.slice(half);
  const personalCount = rows.filter((r) => r.source === "personal").length;
  return (
    <div
      ref={ref}
      style={{
        width: 720,
        padding: "40px 40px 36px",
        background: "linear-gradient(160deg, #143D2F 0%, #1f5340 100%)",
        color: "#fff",
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        borderRadius: 28,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", opacity: 0.7, fontWeight: 800 }}>
            Fairway · Bag IQ
          </div>
          <div style={{ fontSize: 30, fontWeight: 900, lineHeight: 1.05, marginTop: 4 }}>
            {userName ? `${userName.split(" ")[0]}'s yardages` : "My yardages"}
          </div>
          <div style={{ fontSize: 13, opacity: 0.75, marginTop: 4 }}>
            {personalCount > 0
              ? `${personalCount} club${personalCount === 1 ? "" : "s"} learned from real shots`
              : "Bag-average values (log shots to personalise)"}
          </div>
        </div>
        <div
          style={{
            width: 64, height: 64, borderRadius: 18,
            background: "rgba(255,255,255,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 30,
          }}
        >
          🏌️
        </div>
      </div>

      {/* Two-column yardage list */}
      <div style={{ display: "flex", gap: 28 }}>
        {[colA, colB].map((col, ci) => (
          <div key={ci} style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
            {col.map((r) => (
              <div
                key={r.key}
                style={{
                  display: "flex", alignItems: "baseline", justifyContent: "space-between",
                  padding: "10px 14px",
                  borderRadius: 14,
                  background: r.source === "personal"
                    ? "rgba(255,255,255,0.14)"
                    : "rgba(255,255,255,0.06)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {r.source === "personal" && (
                    <span style={{ fontSize: 11, color: "#FFD54A" }}>★</span>
                  )}
                  <span style={{ fontWeight: 800, fontSize: 15, letterSpacing: 0.1 }}>
                    {r.label}
                  </span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontWeight: 900, fontSize: 22, fontVariantNumeric: "tabular-nums" }}>
                    {r.yards}
                  </span>
                  <span style={{ fontSize: 11, opacity: 0.7, marginLeft: 4 }}>yds</span>
                  {r.range && (
                    <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                      range {r.range} · {r.count} shots
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: 28, paddingTop: 18,
          borderTop: "1px solid rgba(255,255,255,0.15)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          fontSize: 11, opacity: 0.7, letterSpacing: 0.4, textTransform: "uppercase", fontWeight: 700,
        }}
      >
        <span>Built in-round from real GPS shots</span>
        <span>fairway-golf.org</span>
      </div>
    </div>
  );
});
